import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  GlobalStyles,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import {
  checkUser,
  friendlyAuthError,
  getEssRoles,
  listEssDepartments,
  registerEss,
  type CheckUserPrefill,
  type DepartmentOption,
  type RoleDefinition,
} from "../api/auth";
import { displayDepartment } from "../api/departments";
import { AUTH_TOKENS as TOKENS } from "../constants/authTokens";
import { EMPLOYEE_ROLE } from "../hooks/useIsAdmin";
import { buildUserUnitOptions } from "../utils/departments";

const GENDER_OPTIONS = ["Male", "Female"] as const;

type Screen = "login" | "register" | "registered";

export default function Login() {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo =
    (location.state as { from?: string } | null)?.from ?? "/suggestions";

  // Cross-screen state — the identifier and password the user typed on
  // the login screen are reused as register-form prefill when checkUser
  // routes them to the register form.
  const [screen, setScreen] = useState<Screen>("login");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [capsOn, setCapsOn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<CheckUserPrefill | null>(null);

  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = TOKENS.bg;
    return () => {
      document.body.style.background = prev;
    };
  }, []);

  const onCapsKey = (e: KeyboardEvent<HTMLInputElement>) => {
    setCapsOn(e.getModifierState && e.getModifierState("CapsLock"));
  };

  // Login-form submit: check whether the typed identifier exists with an
  // ESS role. If yes → password_login. If no → register screen, with
  // the typed identifier prefilled into the appropriate field.
  const onLoginSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await checkUser(identifier.trim());
      if (result.status === "needs_registration") {
        setPrefill(result.prefill ?? null);
        setScreen("register");
        return;
      }
      await login(identifier.trim(), password, remember);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Login flow failed:", err);
      }
      setError(friendlyAuthError(err, "Sign in failed. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          "@keyframes raven-ess-shimmer": {
            "0%": { transform: "translateX(-120%)" },
            "100%": { transform: "translateX(120%)" },
          },
          "@keyframes raven-ess-spin": {
            to: { transform: "rotate(360deg)" },
          },
        }}
      />
      <Box
        sx={{
          minHeight: "100dvh",
          position: "relative",
          overflow: "hidden",
          bgcolor: TOKENS.bg,
          color: TOKENS.ink,
          fontFamily: TOKENS.fontSans,
          WebkitFontSmoothing: "antialiased",
          textRendering: "optimizeLegibility",
        }}
      >
        <Box
          sx={{
            minHeight: "100dvh",
            display: "grid",
            placeItems: "center",
            px: 2,
            py: "88px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="img"
              src="/images/indorama/logo_dark.webp"
              alt="Indorama"
              sx={{
                height: 48,
                width: "auto",
                mb: { xs: "20px", md: "28px" },
                display: "block",
              }}
            />

            {screen === "login" && (
              <LoginCard
                accent={accent}
                identifier={identifier}
                setIdentifier={setIdentifier}
                password={password}
                setPassword={setPassword}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                remember={remember}
                setRemember={setRemember}
                capsOn={capsOn}
                onCapsKey={onCapsKey}
                submitting={submitting}
                error={error}
                onSubmit={onLoginSubmit}
              />
            )}

            {screen === "register" && (
              <RegisterCard
                accent={accent}
                seedIdentifier={identifier}
                seedPassword={password}
                prefill={prefill}
                onBack={() => {
                  setError(null);
                  setScreen("login");
                }}
                onRegistered={() => setScreen("registered")}
              />
            )}

            {screen === "registered" && (
              <RegisteredCard
                accent={accent}
                onBack={() => {
                  setError(null);
                  setScreen("login");
                }}
              />
            )}

            <Box
              sx={{
                width: "100%",
                maxWidth: 460,
                mt: "18px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontFamily: TOKENS.fontMono,
                fontSize: 10.5,
                letterSpacing: ".14em",
                textTransform: "uppercase",
                color: TOKENS.muted,
              }}
            >
              <span>© Raven 2026</span>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

// ----------------------------- Login card -----------------------------

interface LoginCardProps {
  accent: string;
  identifier: string;
  setIdentifier: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  showPassword: boolean;
  setShowPassword: (fn: (prev: boolean) => boolean) => void;
  remember: boolean;
  setRemember: (v: boolean) => void;
  capsOn: boolean;
  onCapsKey: (e: KeyboardEvent<HTMLInputElement>) => void;
  submitting: boolean;
  error: string | null;
  onSubmit: (e: FormEvent) => void;
}

function LoginCard({
  accent,
  identifier,
  setIdentifier,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  remember,
  setRemember,
  capsOn,
  onCapsKey,
  submitting,
  error,
  onSubmit,
}: LoginCardProps) {
  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      autoComplete="on"
      noValidate
      sx={{
        width: "100%",
        maxWidth: 460,
        background: TOKENS.bgElev,
        border: `1px solid ${TOKENS.line}`,
        borderRadius: TOKENS.radius,
        padding: { xs: "24px", md: "36px 36px 30px" },
        boxShadow: TOKENS.shadowCard,
        position: "relative",
      }}
    >
      <CardHeading accent={accent}>Sign in to Indorama ESS</CardHeading>
      <Box
        component="p"
        sx={{
          color: TOKENS.ink2,
          fontSize: 14,
          lineHeight: 1.55,
          m: "0 0 24px",
        }}
      >
        <Box
          component="strong"
          sx={{
            display: "block",
            color: TOKENS.ink,
            fontWeight: 600,
            fontSize: 15,
            letterSpacing: "-0.005em",
            mb: "4px",
          }}
        >
          Use your Indorama email and password.
        </Box>
      </Box>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <FloatingField
        id="identifier"
        label="Email or employee ID"
        type="text"
        autoComplete="username"
        value={identifier}
        onChange={setIdentifier}
        autoFocus
        required
      />

      <FloatingField
        id="password"
        label="Password"
        type={showPassword ? "text" : "password"}
        autoComplete="current-password"
        value={password}
        onChange={setPassword}
        required
        onKeyDown={onCapsKey}
        onKeyUp={onCapsKey}
        endAdornment={
          <Box
            component="button"
            type="button"
            aria-label={showPassword ? "Hide password" : "Show password"}
            onClick={() => setShowPassword((v) => !v)}
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              width: 36,
              height: 36,
              display: "grid",
              placeItems: "center",
              border: "none",
              background: "transparent",
              borderRadius: "10px",
              color: TOKENS.muted,
              cursor: "pointer",
              "&:hover": {
                color: TOKENS.ink,
                background: alpha(TOKENS.ink, 0.06),
              },
            }}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </Box>
        }
      />

      <CapsLockBanner active={capsOn} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          m: "6px 0 22px",
          fontSize: 13,
        }}
      >
        <Box
          component="label"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            color: TOKENS.ink2,
            cursor: "pointer",
            userSelect: "none",
            position: "relative",
          }}
        >
          <Box
            component="input"
            type="checkbox"
            checked={remember}
            onChange={(e) =>
              setRemember((e.target as HTMLInputElement).checked)
            }
            sx={(theme) => ({
              position: "absolute",
              opacity: 0,
              pointerEvents: "none",
              "&:checked + span": {
                background: accent,
                borderColor: accent,
              },
              "&:checked + span::after": {
                content: '""',
                width: 10,
                height: 6,
                borderLeft: `2px solid ${theme.palette.primary.contrastText}`,
                borderBottom: `2px solid ${theme.palette.primary.contrastText}`,
                transform: "rotate(-45deg) translate(1px,-1px)",
              },
            })}
          />
          <Box
            component="span"
            sx={{
              width: 18,
              height: 18,
              borderRadius: "6px",
              border: `1.5px solid ${TOKENS.line}`,
              background: TOKENS.bgElev,
              display: "grid",
              placeItems: "center",
              transition: "all .15s ease",
            }}
          />
          <span>Keep me signed in</span>
        </Box>
      </Box>

      <Button
        type="submit"
        variant="contained"
        color="primary"
        size="medium"
        fullWidth
        disabled={submitting || !identifier || !password}
        endIcon={
          submitting ? undefined : (
            <RavenIcon
              icon={ArrowRight}
              className="arrow"
              sx={{ transition: "transform .18s ease" }}
            />
          )
        }
        sx={{ "&:hover .arrow": { transform: "translateX(3px)" } }}
      >
        {submitting ? (
          <CircularProgress size={18} sx={{ color: "inherit" }} />
        ) : (
          "Sign in"
        )}
      </Button>
    </Box>
  );
}

// ----------------------------- Register card -----------------------------

interface RegisterCardProps {
  accent: string;
  seedIdentifier: string;
  seedPassword: string;
  prefill: CheckUserPrefill | null;
  onBack: () => void;
  onRegistered: () => void;
}

function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function RegisterCard({
  accent,
  seedIdentifier,
  seedPassword,
  prefill,
  onBack,
  onRegistered,
}: RegisterCardProps) {
  const seedIsEmail = looksLikeEmail(seedIdentifier);

  const [name, setName] = useState<string>(prefill?.name ?? "");
  const [email, setEmail] = useState<string>(
    prefill?.email ?? (seedIsEmail ? seedIdentifier : ""),
  );
  const [employeeId, setEmployeeId] = useState<string>(
    prefill?.employee_id ?? (!seedIsEmail ? seedIdentifier : ""),
  );
  const [password, setPassword] = useState<string>(seedPassword);
  const [confirmPassword, setConfirmPassword] = useState<string>(seedPassword);
  const [showPassword, setShowPassword] = useState(false);
  const [unit, setUnit] = useState<string>(prefill?.unit ?? "");
  const [departments, setDepartments] = useState<string[]>(
    prefill?.departments ?? [],
  );
  const [gender, setGender] = useState<string>(prefill?.gender ?? "");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([
    EMPLOYEE_ROLE,
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ESS roles + departments power the register-form dropdowns. Both
  // lists are small and stable, so we cache for the session.
  const rolesQuery = useQuery<RoleDefinition[]>({
    queryKey: ["ess-register-roles"],
    queryFn: getEssRoles,
    staleTime: Infinity,
  });

  const departmentsQuery = useQuery<DepartmentOption[]>({
    queryKey: ["ess-register-departments"],
    queryFn: listEssDepartments,
    staleTime: Infinity,
  });

  const roleLabelByKey = useMemo(() => {
    const map = new Map<string, string>();
    (rolesQuery.data ?? []).forEach((r) => map.set(r.role, r.label));
    return map;
  }, [rolesQuery.data]);

  // Employee is preselected; a backend that does not offer it yet must not
  // be sent a role it cannot grant.
  useEffect(() => {
    if (!rolesQuery.data) return;
    const offered = new Set(rolesQuery.data.map((r) => r.role));
    setSelectedRoles((prev) => {
      const next = prev.filter((role) => offered.has(role));
      return next.length === prev.length ? prev : next;
    });
  }, [rolesQuery.data]);

  // Units come from the department list, so a unit is offered once it has
  // departments configured. A prefilled unit stays selectable even if no
  // department carries it yet.
  const prefillUnit = prefill?.unit ?? "";
  const unitOptions = useMemo(() => {
    const derived = buildUserUnitOptions(departmentsQuery.data ?? []);
    return prefillUnit && !derived.includes(prefillUnit)
      ? [...derived, prefillUnit].sort((a, b) => a.localeCompare(b))
      : derived;
  }, [departmentsQuery.data, prefillUnit]);

  const canSubmit =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    password.length >= 8 &&
    password === confirmPassword &&
    gender.length > 0 &&
    unit.length > 0 &&
    selectedRoles.length > 0 &&
    !submitting;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (email.trim().length === 0) {
      setError("Email is required.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!gender) {
      setError("Gender is required.");
      return;
    }
    if (!unit) {
      setError("Unit is required.");
      return;
    }
    if (selectedRoles.length === 0) {
      setError("Pick at least one ESS role.");
      return;
    }

    setSubmitting(true);
    try {
      const accountId = process.env.REACT_APP_INDORAMA_ACCOUNT_ID;
      await registerEss({
        name: name.trim(),
        password,
        email: email.trim(),
        gender,
        employee_id: employeeId.trim() || undefined,
        account_id: accountId || undefined,
        unit,
        departments: departments.length > 0 ? departments : undefined,
        roles: selectedRoles,
      });
      onRegistered();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("ESS register failed:", err);
      }
      setError(friendlyAuthError(err, "Registration failed. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      autoComplete="on"
      noValidate
      sx={{
        width: "100%",
        maxWidth: 520,
        background: TOKENS.bgElev,
        border: `1px solid ${TOKENS.line}`,
        borderRadius: TOKENS.radius,
        padding: { xs: "24px", md: "32px" },
        boxShadow: TOKENS.shadowCard,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "8px" }}>
        <Button
          type="button"
          variant="text"
          startIcon={<RavenIcon icon={ArrowLeft} />}
          onClick={onBack}
          sx={{
            color: TOKENS.muted,
            textTransform: "none",
            fontWeight: 500,
            ml: "-8px",
          }}
        >
          Sign in
        </Button>
      </Box>

      <CardHeading accent={accent}>Register</CardHeading>
      <Box
        component="p"
        sx={{
          color: TOKENS.ink2,
          fontSize: 14,
          lineHeight: 1.55,
          m: "0 0 20px",
        }}
      >
        {prefill
          ? "We found your profile. Pick the ESS roles you need — an admin will activate your access."
          : "Tell us about yourself and pick the ESS roles you need. An admin will activate your access."}
      </Box>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <FloatingField
        id="reg-name"
        label="Full name"
        type="text"
        autoComplete="name"
        value={name}
        onChange={setName}
        required
      />

      <FloatingField
        id="reg-email"
        label="Email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={setEmail}
        required
      />

      <FloatingField
        id="reg-employee-id"
        label="Employee ID"
        type="text"
        autoComplete="username"
        value={employeeId}
        onChange={setEmployeeId}
      />

      <FloatingField
        id="reg-password"
        label="Password (8+ characters)"
        type={showPassword ? "text" : "password"}
        autoComplete="new-password"
        value={password}
        onChange={setPassword}
        required
        endAdornment={
          <Box
            component="button"
            type="button"
            aria-label={showPassword ? "Hide password" : "Show password"}
            onClick={() => setShowPassword((v) => !v)}
            sx={passwordToggleSx}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </Box>
        }
      />

      <FloatingField
        id="reg-confirm"
        label="Confirm password"
        type={showPassword ? "text" : "password"}
        autoComplete="new-password"
        value={confirmPassword}
        onChange={setConfirmPassword}
        required
      />

      <DropdownField
        id="reg-unit"
        label="Unit"
        value={unit}
        onChange={setUnit}
        options={unitOptions.map((o) => ({ value: o, label: o }))}
        required
      />

      <MultiselectField
        id="reg-departments"
        label="Departments (optional)"
        values={departments}
        onChange={setDepartments}
        options={(departmentsQuery.data ?? [])
          .filter((d) => !unit || !d.unit || d.unit === unit || d.is_common)
          .map((d) => ({ value: d.name, label: displayDepartment(d.name) }))}
        loading={departmentsQuery.isLoading}
        helperText={
          departmentsQuery.isError
            ? "Could not load departments. Try again."
            : unit
              ? `Showing departments for unit ${unit} and common departments.`
              : "Pick one or more departments."
        }
      />

      <DropdownField
        id="reg-gender"
        label="Gender"
        value={gender}
        onChange={setGender}
        options={GENDER_OPTIONS.map((o) => ({ value: o, label: o }))}
        required
      />

      <MultiselectField
        id="reg-roles"
        label="ESS roles"
        values={selectedRoles}
        onChange={setSelectedRoles}
        options={(rolesQuery.data ?? []).map((r) => ({
          value: r.role,
          label: r.label,
        }))}
        loading={rolesQuery.isLoading}
        helperText={
          rolesQuery.isError
            ? "Could not load ESS roles. Try again."
            : "Employee is enough to submit and follow your own suggestions. Add a role only if you hold it — an admin reviews and activates."
        }
        renderChip={(value) => roleLabelByKey.get(value) ?? value}
        required
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        size="medium"
        fullWidth
        disabled={!canSubmit}
        sx={{ mt: 1 }}
      >
        {submitting ? (
          <CircularProgress size={18} sx={{ color: "inherit" }} />
        ) : (
          "Register"
        )}
      </Button>
    </Box>
  );
}

// ----------------------------- Registered screen -----------------------------

function RegisteredCard({
  accent,
  onBack,
}: {
  accent: string;
  onBack: () => void;
}) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 460,
        background: TOKENS.bgElev,
        border: `1px solid ${TOKENS.line}`,
        borderRadius: TOKENS.radius,
        padding: { xs: "24px", md: "36px" },
        boxShadow: TOKENS.shadowCard,
        textAlign: "center",
      }}
    >
      <CardHeading accent={accent}>Request submitted</CardHeading>
      <Alert
        severity="info"
        sx={{ textAlign: "left", borderRadius: "10px", mb: 3 }}
      >
        Your registration is in. An admin will activate your access shortly —
        once they verify you, sign in with the password you just set.
      </Alert>
      <Button variant="contained" color="primary" fullWidth onClick={onBack}>
        Back to sign in
      </Button>
    </Box>
  );
}

// ----------------------------- Shared bits -----------------------------

function CardHeading({
  accent,
  children,
}: {
  accent: string;
  children: ReactNode;
}) {
  return (
    <Box
      component="h2"
      sx={{
        fontFamily: TOKENS.fontSans,
        fontWeight: 700,
        fontSize: 30,
        lineHeight: 1.15,
        letterSpacing: "-0.02em",
        m: "4px 0 10px",
        textWrap: "balance",
        color: TOKENS.ink,
        "& em": {
          color: accent,
          fontStyle: "normal",
          fontWeight: 700,
        },
      }}
    >
      {children}
      <Box component="em">.</Box>
    </Box>
  );
}

function ErrorBanner({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 14px",
        mb: "14px",
        borderRadius: "12px",
        border: `1px solid color-mix(in oklab, ${TOKENS.warn} 40%, ${TOKENS.line})`,
        background: `color-mix(in oklab, ${TOKENS.warn} 8%, transparent)`,
        color: TOKENS.warn,
        fontSize: 13,
        fontWeight: 500,
      }}
      role="alert"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
      </svg>
      <span>{children}</span>
    </Box>
  );
}

function CapsLockBanner({ active }: { active: boolean }) {
  return (
    <Box
      aria-hidden={!active}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontFamily: TOKENS.fontMono,
        fontSize: 11,
        letterSpacing: ".12em",
        textTransform: "uppercase",
        color: TOKENS.warn,
        border: `1px dashed color-mix(in oklab, ${TOKENS.warn} 50%, ${TOKENS.line})`,
        borderRadius: "10px",
        background: `color-mix(in oklab, ${TOKENS.warn} 8%, transparent)`,
        transition: "opacity .18s, transform .18s, max-height .18s",
        overflow: "hidden",
        ...(active
          ? {
              opacity: 1,
              transform: "none",
              maxHeight: 40,
              padding: "8px 10px",
              m: "-4px 0 10px",
            }
          : {
              opacity: 0,
              transform: "translateY(-4px)",
              maxHeight: 0,
              padding: "0 10px",
              m: 0,
              pointerEvents: "none",
            }),
      }}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 3l8 9h-5v6h-6v-6H4z" />
      </svg>
      Caps Lock is on
    </Box>
  );
}

const passwordToggleSx = {
  position: "absolute" as const,
  right: 8,
  top: "50%",
  transform: "translateY(-50%)",
  width: 36,
  height: 36,
  display: "grid",
  placeItems: "center",
  border: "none",
  background: "transparent",
  borderRadius: "10px",
  color: TOKENS.muted,
  cursor: "pointer",
  "&:hover": {
    color: TOKENS.ink,
    background: alpha(TOKENS.ink, 0.06),
  },
};

// ----------------------------- Floating field -----------------------------

type FloatingFieldProps = {
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  autoFocus?: boolean;
  required?: boolean;
  endAdornment?: ReactNode;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (e: KeyboardEvent<HTMLInputElement>) => void;
};

function FloatingField({
  id,
  label,
  type,
  value,
  onChange,
  autoComplete,
  autoFocus,
  required,
  endAdornment,
  onKeyDown,
  onKeyUp,
}: FloatingFieldProps) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const accentSoft = alpha(theme.palette.primary.main, 0.18);
  return (
    <Box
      sx={{
        position: "relative",
        mb: "14px",
        "& input": {
          width: "100%",
          height: 56,
          padding: endAdornment ? "22px 48px 8px 16px" : "22px 16px 8px",
          font: `500 15px/1 ${TOKENS.fontSans}`,
          color: TOKENS.ink,
          background: TOKENS.bg,
          border: `1px solid ${TOKENS.line}`,
          borderRadius: TOKENS.radiusSm,
          outline: "none",
          transition:
            "border-color .18s ease, box-shadow .18s ease, background .18s ease",
        },
        "& input:focus, & input:not(:placeholder-shown)": {
          borderColor: accent,
          background: TOKENS.bgElev,
        },
        "& input:focus": {
          boxShadow: `0 0 0 4px ${accentSoft}`,
        },
        "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:active":
          {
            WebkitBoxShadow: `0 0 0 1000px ${TOKENS.bgElev} inset`,
            WebkitTextFillColor: TOKENS.ink,
            caretColor: TOKENS.ink,
            borderColor: accent,
            transition:
              "background-color 9999s ease-in-out 0s, color 9999s ease-in-out 0s",
          },
        "& input:-webkit-autofill:focus": {
          WebkitBoxShadow: `0 0 0 1000px ${TOKENS.bgElev} inset, 0 0 0 4px ${accentSoft}`,
          WebkitTextFillColor: TOKENS.ink,
          caretColor: TOKENS.ink,
          borderColor: accent,
        },
        "& label": {
          position: "absolute",
          left: 16,
          top: 18,
          fontSize: 14,
          color: TOKENS.muted,
          pointerEvents: "none",
          transition: "all .18s ease",
          background: "transparent",
          padding: "0 4px",
        },
        "& input:focus + label, & input:not(:placeholder-shown) + label": {
          top: "-7px",
          left: "12px",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: ".04em",
          color: accent,
          background: TOKENS.bgElev,
        },
      }}
    >
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        required={required}
        placeholder=" "
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
      />
      <label htmlFor={id}>
        {label}
        {required && (
          <Box component="span" sx={{ color: accent, ml: "2px" }}>
            *
          </Box>
        )}
      </label>
      {endAdornment}
    </Box>
  );
}

// ----------------------------- Dropdown field -----------------------------

interface DropdownFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
}

function DropdownField({
  id,
  label,
  value,
  onChange,
  options,
  required,
}: DropdownFieldProps) {
  return (
    <FormControl fullWidth required={required} sx={{ mb: "14px" }}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        value={value}
        label={label}
        onChange={(e) => onChange(e.target.value)}
      >
        {!required && (
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
        )}
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// ----------------------------- Multiselect field -----------------------------

interface MultiselectFieldProps {
  id: string;
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  options: { value: string; label: string }[];
  helperText?: string;
  loading?: boolean;
  required?: boolean;
  renderChip?: (value: string) => string;
}

function MultiselectField({
  id,
  label,
  values,
  onChange,
  options,
  helperText,
  loading,
  required,
  renderChip,
}: MultiselectFieldProps) {
  const handleSelectChange = (e: SelectChangeEvent<string[]>) => {
    const raw = e.target.value;
    onChange(typeof raw === "string" ? raw.split(",") : raw);
  };

  return (
    <FormControl fullWidth required={required} sx={{ mb: "14px" }}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        multiple
        value={values}
        onChange={handleSelectChange}
        label={label}
        renderValue={(selected) =>
          (selected as string[])
            .map((v) => (renderChip ? renderChip(v) : v))
            .join(", ")
        }
      >
        {loading ? (
          <MenuItem disabled value="">
            Loading…
          </MenuItem>
        ) : options.length === 0 ? (
          <MenuItem disabled value="">
            No options available
          </MenuItem>
        ) : (
          options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              <Checkbox checked={values.includes(opt.value)} />
              <ListItemText primary={opt.label} />
            </MenuItem>
          ))
        )}
      </Select>
      {helperText && (
        <Box
          component="span"
          sx={{ display: "block", fontSize: 12, color: TOKENS.muted, mt: "6px" }}
        >
          {helperText}
        </Box>
      )}
    </FormControl>
  );
}

// ----------------------------- Eye icons -----------------------------

function EyeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 19c-6.5 0-10-7-10-7a19.77 19.77 0 0 1 4.22-5.38" />
      <path d="M1 1l22 22" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a19.5 19.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    </svg>
  );
}
